import { useEffect, useMemo, useState } from 'react'
import {
  FaArrowLeft,
  FaBullhorn,
  FaChartLine,
  FaDollarSign,
  FaCompass,
  FaEye,
  FaMapMarkerAlt,
  FaRoute,
  FaSatelliteDish,
  FaShieldAlt,
  FaSignOutAlt,
  FaStar,
  FaUser,
  FaUsers
} from 'react-icons/fa'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useAuth } from 'contexts/auth.context'
import { useUser } from 'contexts/user.context'

import api from 'services/api'

import * as S from '../style'

type LocalityStatus = 'active' | 'hidden'

type AdminLocality = {
  destinationKey: string
  sourceName: string
  displayName: string
  slug?: string | null
  latitude: number
  longitude: number
  occurrences: number
  itineraryCount: number
  publicItineraryCount: number
  views: number
  status: LocalityStatus
  featured: boolean
  countryCode?: string | null
  countryName?: string | null
  editorialStatus?: 'legacy' | 'auto' | 'reviewed' | 'manual'
  editorialLocked?: boolean
  editorialGeneratedAt?: string | null
  moderationUpdatedAt?: string | null
}

type LocalitiesResponse = {
  items: AdminLocality[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
  summary: {
    total: number
    active: number
    hidden: number
    featured: number
  }
}

type LocalityDetails = AdminLocality & {
  description?: string | null
  seoTitle?: string | null
  seoDescription?: string | null
  editorialSourceType?: string | null
  editorialSourceId?: string | null
  editorialSourceTitle?: string | null
  editorialSourceUrl?: string | null
  editorialSourceLanguage?: string | null
  editorialSourceConfidence?: number | null
  editorialModel?: string | null
  editorialInputTokens?: number
  editorialOutputTokens?: number
  editorialCostUsd?: number | null
  editorialReviewedAt?: string | null
  legacyDescription?: string | null
  routes: Array<{
    id: string
    title: string
    made: boolean
    createdAt: string
    authorFirstname: string
    authorLastname: string
    moderationStatus: 'visible' | 'hidden'
  }>
  variants: Array<{
    name: string
    occurrences: number
  }>
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  return new Date(value).toLocaleDateString('pt-BR')
}

const formatCoordinates = (latitude: number, longitude: number) =>
  `${Number(latitude).toFixed(4)}, ${Number(longitude).toFixed(4)}`

const fetchLocalityDetails = async (destinationKey: string) => {
  const response = await api.get<LocalityDetails>(
    `/admin/localities/${destinationKey}`
  )
  return response.data
}

const AdminLocalities = () => {
  const { user } = useUser()
  const { signOut } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [featured, setFeatured] = useState('all')
  const [page, setPage] = useState(1)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const [displayName, setDisplayName] = useState('')
  const [nextStatus, setNextStatus] = useState<LocalityStatus>('active')
  const [nextFeatured, setNextFeatured] = useState(false)
  const [description, setDescription] = useState('')
  const [seoTitle, setSeoTitle] = useState('')
  const [seoDescription, setSeoDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [generatingEditorial, setGeneratingEditorial] = useState(false)
  const [reviewingEditorial, setReviewingEditorial] = useState(false)
  const [editorialSuccess, setEditorialSuccess] = useState('')

  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ')

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 450)

    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [status, featured])

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '20',
      status,
      featured
    })

    if (debouncedSearch) params.set('search', debouncedSearch)
    return params.toString()
  }, [debouncedSearch, featured, page, status])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-localities', queryString],
    queryFn: async () => {
      const response = await api.get<LocalitiesResponse>(
        `/admin/localities?${queryString}`
      )
      return response.data
    },
    staleTime: 30 * 1000
  })

  const { data: selected, isLoading: loadingDetails } = useQuery({
    queryKey: ['admin-locality-details', selectedKey],
    queryFn: () => fetchLocalityDetails(selectedKey as string),
    enabled: Boolean(selectedKey),
    staleTime: 60 * 1000
  })

  const prefetchLocality = (destinationKey: string) => {
    void queryClient.prefetchQuery({
      queryKey: ['admin-locality-details', destinationKey],
      queryFn: () => fetchLocalityDetails(destinationKey),
      staleTime: 60 * 1000
    })
  }

  useEffect(() => {
    if (!selected) return

    setDisplayName(selected.displayName || selected.sourceName)
    setNextStatus(selected.status)
    setNextFeatured(selected.featured)
    setDescription(selected.description || '')
    setSeoTitle(selected.seoTitle || '')
    setSeoDescription(selected.seoDescription || '')
  }, [selected])

  const closeDetails = () => {
    if (saving || generatingEditorial || reviewingEditorial) return
    setSelectedKey(null)
  }

  const saveLocality = async () => {
    if (!selectedKey || saving) return

    setSaving(true)

    try {
      await api.patch(`/admin/localities/${selectedKey}`, {
        displayName,
        status: nextStatus,
        featured: nextFeatured,
        description,
        seoTitle,
        seoDescription
      })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-localities'] }),
        queryClient.invalidateQueries({
          queryKey: ['admin-locality-details', selectedKey]
        }),
        queryClient.invalidateQueries({
          queryKey: ['destination-catalog-state']
        })
      ])

      toast.success('Localidade atualizada.', { theme: 'dark' })
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível atualizar esta localidade.',
        { theme: 'dark' }
      )
    } finally {
      setSaving(false)
    }
  }

  const refreshLocalityQueries = async () => {
    if (!selectedKey) return

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-localities'] }),
      queryClient.invalidateQueries({
        queryKey: ['admin-locality-details', selectedKey]
      }),
      queryClient.invalidateQueries({
        queryKey: ['destination-catalog-state']
      }),
      queryClient.invalidateQueries({
        queryKey: ['destination-page']
      })
    ])
  }

  const generateEditorial = async () => {
    if (!selectedKey || generatingEditorial) return

    setEditorialSuccess('')
    const force = Boolean(selected?.editorialLocked)
    if (
      force &&
      !window.confirm(
        'Este conteúdo está protegido porque foi revisado ou editado manualmente. Deseja substituir pelo novo texto gerado?'
      )
    ) {
      return
    }

    setGeneratingEditorial(true)
    try {
      const response = await api.post(
        `/admin/localities/${selectedKey}/editorial/generate`,
        { force },
        { timeout: 120000 }
      )

      const locality = response.data?.locality as LocalityDetails | undefined
      if (locality) {
        setDescription(locality.description || '')
        setSeoTitle(locality.seoTitle || '')
        setSeoDescription(locality.seoDescription || '')
      }

      await refreshLocalityQueries()
      const cost = Number(
        response.data?.generation?.telemetry?.estimatedCostUsd ?? 0
      )
      const successMessage = 'Conteúdo gerado e validado com sucesso.'
      setEditorialSuccess(successMessage)
      window.setTimeout(() => setEditorialSuccess(''), 10000)

      toast.success(
        cost
          ? `${successMessage} Custo estimado: US$ ${cost.toFixed(4)}.`
          : successMessage,
        { theme: 'dark' }
      )
    } catch (error: any) {
      const timedOut =
        error?.code === 'ECONNABORTED' ||
        error?.code === 'ETIMEDOUT' ||
        String(error?.message || '')
          .toLowerCase()
          .includes('timeout')

      if (timedOut && selectedKey) {
        try {
          const latest = await fetchLocalityDetails(selectedKey)

          if (
            latest?.editorialStatus === 'auto' &&
            latest?.editorialGeneratedAt
          ) {
            setDescription(latest.description || '')
            setSeoTitle(latest.seoTitle || '')
            setSeoDescription(latest.seoDescription || '')
            await refreshLocalityQueries()
            const successMessage = 'Conteúdo gerado e validado com sucesso.'
            setEditorialSuccess(successMessage)
            window.setTimeout(() => setEditorialSuccess(''), 10000)
            toast.success(
              'A geração concluiu no servidor e o conteúdo foi atualizado.',
              { theme: 'dark' }
            )
            return
          }
        } catch {
          // Keep the timeout feedback below if confirmation cannot be loaded.
        }

        toast.error(
          'A geração ultrapassou 2 minutos. Atualize a localidade antes de tentar novamente para evitar uma geração duplicada.',
          { theme: 'dark', autoClose: 8000 }
        )
        return
      }

      toast.error(
        error?.response?.data?.message ||
          'Não foi possível gerar o conteúdo desta localidade. Tente novamente.',
        { theme: 'dark', autoClose: 7000 }
      )
    } finally {
      setGeneratingEditorial(false)
    }
  }

  const reviewEditorial = async () => {
    if (!selectedKey || reviewingEditorial) return

    setReviewingEditorial(true)
    try {
      await api.post(`/admin/localities/${selectedKey}/editorial/review`)
      await refreshLocalityQueries()
      toast.success('Conteúdo marcado como revisado e protegido.', {
        theme: 'dark'
      })
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          'Não foi possível revisar este conteúdo.',
        { theme: 'dark' }
      )
    } finally {
      setReviewingEditorial(false)
    }
  }

  const editorialStatusLabel = (value?: LocalityDetails['editorialStatus']) =>
    ({
      legacy: 'Legado · ainda não validado',
      auto: 'Gerado por IA · aguardando revisão',
      reviewed: 'Revisado · protegido',
      manual: 'Manual · protegido'
    })[value || 'legacy']

  return (
    <S.Page>
      <S.Sidebar>
        <S.BrandBlock>
          <S.BrandLink to="/">
            <S.Logo src="/images/header-logo.png" alt="RedSterna" />
          </S.BrandLink>
          <S.AdminBadge>
            <FaShieldAlt />
            Admin
          </S.AdminBadge>
        </S.BrandBlock>

        <S.SidebarBody>
          <S.SidebarLabel>Gestão</S.SidebarLabel>
          <S.SidebarNav>
            <S.SidebarNavLink to="/admin">
              <FaChartLine />
              Dashboard
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/usuarios">
              <FaUsers />
              Usuários
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/localidades" $active>
              <FaMapMarkerAlt />
              Localidades
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/roteiros">
              <FaRoute />
              Roteiros
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/anuncios">
              <FaBullhorn />
              Anúncios
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/radar">
              <FaSatelliteDish />
              Radar
            </S.SidebarNavLink>
            <S.SidebarNavLink to="/admin/comissoes">
              <FaDollarSign />
              Comissões
            </S.SidebarNavLink>
          </S.SidebarNav>
        </S.SidebarBody>

        <S.SidebarFooter>
          <S.SidebarLink to="/minha-conta">
            <FaUser />
            Meu perfil
          </S.SidebarLink>
          <S.SidebarLink to="/">
            <FaArrowLeft />
            Voltar ao site
          </S.SidebarLink>
        </S.SidebarFooter>
      </S.Sidebar>

      <S.Main>
        <S.Topbar>
          <div>
            <S.TopbarEyebrow>RedSterna Admin</S.TopbarEyebrow>
            <S.TopbarTitle>Localidades</S.TopbarTitle>
          </div>

          <S.UserArea>
            <S.UserAvatar>
              {user?.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={fullName || 'Administrador'}
                />
              ) : (
                <FaUser />
              )}
            </S.UserAvatar>
            <S.UserInfo>
              <strong>{fullName || 'Administrador'}</strong>
              <span>{user?.email}</span>
            </S.UserInfo>
            <S.AdminChip>Admin</S.AdminChip>
            <S.SignOutButton type="button" onClick={signOut} aria-label="Sair">
              <FaSignOutAlt />
            </S.SignOutButton>
          </S.UserArea>
        </S.Topbar>

        <S.Content>
          <S.DashboardHeader>
            <div>
              <h1>Gestão de localidades</h1>
              <p>
                Administre os destinos consolidados a partir dos roteiros da
                comunidade.
              </p>
            </div>
          </S.DashboardHeader>

          {data && (
            <S.UserSummaryGrid>
              <S.UserSummaryCard>
                <FaMapMarkerAlt />
                <div>
                  <strong>{data.summary.total}</strong>
                  <span>Localidades</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="green">
                <FaEye />
                <div>
                  <strong>{data.summary.active}</strong>
                  <span>Ativas</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="blue">
                <FaStar />
                <div>
                  <strong>{data.summary.featured}</strong>
                  <span>Em destaque</span>
                </div>
              </S.UserSummaryCard>
              <S.UserSummaryCard $tone="red">
                <FaShieldAlt />
                <div>
                  <strong>{data.summary.hidden}</strong>
                  <span>Ocultas</span>
                </div>
              </S.UserSummaryCard>
            </S.UserSummaryGrid>
          )}

          <S.UserToolbar>
            <S.UserSearchInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar localidade..."
            />
            <S.UserFilter
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativas</option>
              <option value="hidden">Ocultas</option>
            </S.UserFilter>
            <S.UserFilter
              value={featured}
              onChange={(event) => setFeatured(event.target.value)}
            >
              <option value="all">Destaque: todos</option>
              <option value="yes">Em destaque</option>
              <option value="no">Sem destaque</option>
            </S.UserFilter>
          </S.UserToolbar>

          {isLoading && <S.StateCard>Carregando localidades...</S.StateCard>}

          {isError && (
            <S.StateCard $error>
              Não foi possível carregar as localidades.
            </S.StateCard>
          )}

          {data && (
            <S.UserTableCard>
              <S.LocalityTable>
                <thead>
                  <tr>
                    <th>Localidade</th>
                    <th>Status</th>
                    <th>Roteiros</th>
                    <th>Views</th>
                    <th>Ocorrências</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item) => (
                    <tr key={item.destinationKey}>
                      <td>
                        <S.LocalityIdentity>
                          <FaMapMarkerAlt />
                          <div>
                            <strong>{item.displayName}</strong>
                            <span>
                              {formatCoordinates(item.latitude, item.longitude)}
                            </span>
                          </div>
                        </S.LocalityIdentity>
                      </td>
                      <td>
                        <S.ModerationChip
                          $status={
                            item.status === 'hidden' ? 'hidden' : 'visible'
                          }
                        >
                          {item.status === 'hidden' ? 'Oculta' : 'Ativa'}
                        </S.ModerationChip>
                        {item.featured && (
                          <S.LocalityFeatured>
                            <FaStar /> Destaque
                          </S.LocalityFeatured>
                        )}
                      </td>
                      <td>
                        {item.publicItineraryCount} públicos ·{' '}
                        {item.itineraryCount} total
                      </td>
                      <td>{item.views}</td>
                      <td>{item.occurrences}</td>
                      <td>
                        <S.TableAction
                          type="button"
                          onMouseEnter={() =>
                            prefetchLocality(item.destinationKey)
                          }
                          onFocus={() => prefetchLocality(item.destinationKey)}
                          onClick={() => setSelectedKey(item.destinationKey)}
                        >
                          Gerenciar
                        </S.TableAction>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </S.LocalityTable>

              <S.MobileLocalityList>
                {data.items.map((item) => (
                  <S.MobileLocalityCard key={item.destinationKey}>
                    <S.MobileLocalityHeader>
                      <S.LocalityIdentity>
                        <FaMapMarkerAlt />
                        <div>
                          <strong>{item.displayName}</strong>
                          <span>
                            {formatCoordinates(item.latitude, item.longitude)}
                          </span>
                        </div>
                      </S.LocalityIdentity>
                      <S.TableAction
                        type="button"
                        onMouseEnter={() =>
                          prefetchLocality(item.destinationKey)
                        }
                        onFocus={() => prefetchLocality(item.destinationKey)}
                        onClick={() => setSelectedKey(item.destinationKey)}
                      >
                        Gerenciar
                      </S.TableAction>
                    </S.MobileLocalityHeader>

                    <S.MobileUserBadges>
                      <S.ModerationChip
                        $status={
                          item.status === 'hidden' ? 'hidden' : 'visible'
                        }
                      >
                        {item.status === 'hidden' ? 'Oculta' : 'Ativa'}
                      </S.ModerationChip>
                      {item.featured && (
                        <S.LocalityFeatured>
                          <FaStar /> Destaque
                        </S.LocalityFeatured>
                      )}
                    </S.MobileUserBadges>

                    <S.MobileLocalityMetrics>
                      <div>
                        <strong>{item.publicItineraryCount}</strong>
                        <span>Roteiros públicos</span>
                      </div>
                      <div>
                        <strong>{item.views}</strong>
                        <span>Visualizações</span>
                      </div>
                      <div>
                        <strong>{item.occurrences}</strong>
                        <span>Registros</span>
                      </div>
                    </S.MobileLocalityMetrics>
                  </S.MobileLocalityCard>
                ))}
              </S.MobileLocalityList>

              {data.items.length === 0 && (
                <S.EmptyText>
                  Nenhuma localidade encontrada com os filtros selecionados.
                </S.EmptyText>
              )}

              <S.Pagination>
                <span>
                  {data.pagination.total} localidades · página{' '}
                  {data.pagination.page} de {data.pagination.pages}
                </span>
                <div>
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={page >= data.pagination.pages}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </S.Pagination>
            </S.UserTableCard>
          )}
        </S.Content>
      </S.Main>

      {selectedKey && (
        <S.AdminModalOverlay onMouseDown={closeDetails}>
          <S.AdminModal
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <S.AdminModalHeader>
              <div>
                <S.TopbarEyebrow>Gestão de destino</S.TopbarEyebrow>
                <h2>Detalhes da localidade</h2>
              </div>
              <button type="button" onClick={closeDetails}>
                ×
              </button>
            </S.AdminModalHeader>

            {loadingDetails || !selected ? (
              <S.StateCard>Carregando localidade...</S.StateCard>
            ) : (
              <>
                <S.LocalityHero>
                  <FaMapMarkerAlt />
                  <div>
                    <h3>{selected.displayName}</h3>
                    <p>{selected.sourceName}</p>
                    <span>
                      {formatCoordinates(selected.latitude, selected.longitude)}
                    </span>
                    {selected.slug && (
                      <a
                        href={`/destinos/${selected.slug}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        /destinos/{selected.slug}
                      </a>
                    )}
                  </div>
                </S.LocalityHero>

                <S.AdminUserStats>
                  <div>
                    <strong>{selected.itineraryCount}</strong>
                    <span>Roteiros</span>
                  </div>
                  <div>
                    <strong>{selected.views}</strong>
                    <span>Visualizações</span>
                  </div>
                  <div>
                    <strong>{selected.occurrences}</strong>
                    <span>Ocorrências</span>
                  </div>
                  <div>
                    <strong>{selected.variants.length}</strong>
                    <span>Variações</span>
                  </div>
                </S.AdminUserStats>

                <S.LocalityForm>
                  <label>
                    Nome exibido
                    <input
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                    />
                  </label>

                  <label>
                    Status
                    <select
                      value={nextStatus}
                      onChange={(event) =>
                        setNextStatus(event.target.value as LocalityStatus)
                      }
                    >
                      <option value="active">Ativa</option>
                      <option value="hidden">Oculta</option>
                    </select>
                  </label>

                  <S.CheckLabel>
                    <input
                      type="checkbox"
                      checked={nextFeatured}
                      onChange={(event) =>
                        setNextFeatured(event.target.checked)
                      }
                    />
                    Destacar esta localidade
                  </S.CheckLabel>

                  {selected.slug && (
                    <S.AdminNotice>
                      URL canônica: /destinos/{selected.slug}. Localidades em
                      destaque aparecem antes das demais no catálogo público.
                    </S.AdminNotice>
                  )}

                  <S.ItinerarySummaryBox>
                    <strong>Conteúdo editorial do destino</strong>
                    <span>
                      {editorialStatusLabel(selected.editorialStatus)}
                    </span>
                    {selected.countryName && (
                      <span>
                        Identidade: {selected.displayName},{' '}
                        {selected.countryName}
                      </span>
                    )}
                    {selected.editorialSourceId && (
                      <span>
                        Fonte validada: {selected.editorialSourceId}
                        {selected.editorialSourceTitle
                          ? ` · ${selected.editorialSourceTitle}`
                          : ''}
                      </span>
                    )}
                    {selected.editorialSourceUrl && (
                      <a
                        href={selected.editorialSourceUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Conferir fonte utilizada
                      </a>
                    )}
                    {selected.editorialGeneratedAt && (
                      <span>
                        Gerado em {formatDate(selected.editorialGeneratedAt)}
                        {selected.editorialModel
                          ? ` · ${selected.editorialModel}`
                          : ''}
                        {selected.editorialCostUsd !== null &&
                        selected.editorialCostUsd !== undefined
                          ? ` · custo estimado US$ ${Number(
                              selected.editorialCostUsd
                            ).toFixed(4)}`
                          : ''}
                      </span>
                    )}
                    <div>
                      <S.TableAction
                        type="button"
                        disabled={generatingEditorial}
                        onClick={generateEditorial}
                      >
                        {generatingEditorial
                          ? 'Gerando e validando...'
                          : selected.editorialLocked
                            ? 'Regenerar com IA'
                            : 'Gerar com IA'}
                      </S.TableAction>{' '}
                      {selected.editorialStatus === 'auto' && (
                        <S.TableAction
                          type="button"
                          disabled={reviewingEditorial}
                          onClick={reviewEditorial}
                        >
                          {reviewingEditorial
                            ? 'Revisando...'
                            : 'Marcar como revisado'}
                        </S.TableAction>
                      )}
                    </div>
                    {generatingEditorial && (
                      <S.AdminNotice role="status" aria-live="polite">
                        Gerando e validando conteúdo, fonte e SEO. Esse processo
                        normalmente leva alguns segundos e pode demorar até
                        cerca de 2 minutos. Mantenha esta tela aberta até
                        receber a confirmação de sucesso ou erro.
                      </S.AdminNotice>
                    )}
                    {editorialSuccess && (
                      <S.AdminNotice role="status" aria-live="polite">
                        ✓ {editorialSuccess}
                      </S.AdminNotice>
                    )}
                    <small>
                      A identidade é validada por nome, país e coordenadas antes
                      da geração. Edições manuais e conteúdos revisados ficam
                      protegidos contra substituição automática.
                    </small>
                  </S.ItinerarySummaryBox>

                  <label className="wide">
                    Descrição editorial
                    <textarea
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder="Descrição própria da RedSterna para a ficha do destino."
                    />
                  </label>

                  <label>
                    Título SEO
                    <input
                      value={seoTitle}
                      onChange={(event) => setSeoTitle(event.target.value)}
                    />
                    <small>
                      Gerado automaticamente quando o destino é criado. Você
                      pode editar este texto.
                    </small>
                  </label>

                  <label>
                    Descrição SEO
                    <textarea
                      value={seoDescription}
                      onChange={(event) =>
                        setSeoDescription(event.target.value)
                      }
                    />
                    <small>
                      Gerada automaticamente com uma descrição natural para
                      busca. Você pode personalizar.
                    </small>
                  </label>
                </S.LocalityForm>

                {nextStatus === 'hidden' && (
                  <S.AdminNotice>
                    A localidade deixará de aparecer no catálogo público de
                    Destinos, mas continuará existindo dentro dos roteiros que
                    já a utilizam.
                  </S.AdminNotice>
                )}

                {selected.variants.length > 1 && (
                  <S.ItinerarySummaryBox>
                    <strong>Variações encontradas</strong>
                    <S.CollaboratorList>
                      {selected.variants.map((variant) => (
                        <span key={variant.name}>
                          {variant.name} · {variant.occurrences} ocorrências
                        </span>
                      ))}
                    </S.CollaboratorList>
                  </S.ItinerarySummaryBox>
                )}

                <S.ItinerarySummaryBox>
                  <strong>Roteiros relacionados</strong>
                  <S.LocalityRouteList>
                    {selected.routes.length ? (
                      selected.routes.map((route) => (
                        <a
                          key={route.id}
                          href={`/roteiro/${route.id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <div>
                            <strong>{route.title}</strong>
                            <span>
                              {route.authorFirstname} {route.authorLastname} ·{' '}
                              {route.made ? 'Realizada' : 'Planejada'} ·{' '}
                              {formatDate(route.createdAt)}
                            </span>
                          </div>
                          <S.ModerationChip
                            $status={
                              route.moderationStatus === 'hidden'
                                ? 'hidden'
                                : 'visible'
                            }
                          >
                            {route.moderationStatus === 'hidden'
                              ? 'Oculto'
                              : 'Visível'}
                          </S.ModerationChip>
                        </a>
                      ))
                    ) : (
                      <span>Nenhum roteiro relacionado.</span>
                    )}
                  </S.LocalityRouteList>
                </S.ItinerarySummaryBox>

                <S.AdminModalActions>
                  <button type="button" onClick={closeDetails}>
                    Fechar
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={saving}
                    onClick={saveLocality}
                  >
                    {saving ? 'Salvando...' : 'Salvar localidade'}
                  </button>
                </S.AdminModalActions>
              </>
            )}
          </S.AdminModal>
        </S.AdminModalOverlay>
      )}
    </S.Page>
  )
}

export default AdminLocalities
